import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { Badge } from '../ui/badge';
import { MapPin, Search, Loader2, CheckCircle2, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';

const TerritoryCheckerWidget = ({ open, onClose }) => {
  const { api } = useAuth();
  const [pinCode, setPinCode] = useState('');
  const [city, setCity] = useState('');
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState(null);

  const handleCheck = async () => {
    if (!pinCode && !city) return toast.error('Enter a pincode or city');
    setChecking(true);
    setResult(null);
    try {
      const params = new URLSearchParams();
      if (pinCode) params.set('pinCode', pinCode);
      if (city) params.set('city', city);
      const { data } = await api.get(`/franchise-outlets/check?${params}`);
      setResult(data);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Check failed';
      toast.error(typeof msg === 'string' ? msg : 'Check failed');
    } finally {
      setChecking(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleCheck();
  };

  const reset = () => {
    setPinCode(''); setCity(''); setResult(null);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { onClose(); reset(); } }}>
      <DialogContent className="sm:max-w-lg" data-testid="territory-checker-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-emerald-600" /> Check Territory Availability
          </DialogTitle>
          <DialogDescription>
            Type a 6-digit pincode or city name to instantly see if a Servall outlet
            already exists in that area.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Pincode</Label>
              <Input value={pinCode} onChange={(e) => setPinCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                onKeyDown={handleKeyDown} placeholder="e.g. 462001" data-testid="check-pincode" />
            </div>
            <div>
              <Label>City</Label>
              <Input value={city} onChange={(e) => setCity(e.target.value)}
                onKeyDown={handleKeyDown} placeholder="e.g. Bhopal" data-testid="check-city" />
            </div>
          </div>
          <Button onClick={handleCheck} disabled={checking} className="w-full" size="lg"
            data-testid="check-territory-btn">
            {checking ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
            Check Availability
          </Button>

          {result && (
            <div className="mt-2">
              {result.available ? (
                <Alert className="border-emerald-300 bg-emerald-50" data-testid="territory-open">
                  <CheckCircle2 className="h-4 w-4 text-emerald-700" />
                  <AlertTitle className="text-emerald-800">
                    🟢 Territory is OPEN!
                  </AlertTitle>
                  <AlertDescription className="text-emerald-700">
                    No active Servall outlet exists at this {result.queriedPin ? 'pincode' : 'city'}.
                    Proceed with the franchise pitch.
                  </AlertDescription>
                </Alert>
              ) : (
                <Alert variant="destructive" data-testid="territory-blocked">
                  <ShieldAlert className="h-4 w-4" />
                  <AlertTitle>🔴 Already allocated</AlertTitle>
                  <AlertDescription>
                    Franchise NOT available in this area.
                    <div className="mt-2 space-y-1">
                      {result.conflicts.map((c, i) => (
                        <div key={i} className="text-xs flex items-center gap-2 bg-white/50 rounded px-2 py-1">
                          <Badge variant="outline" className="text-[10px]">
                            {c.source === 'leads' ? 'Outlet (Lead)' : c.status}
                          </Badge>
                          <strong>{c.outletName}</strong>
                          {c.franchiseeName && c.franchiseeName !== c.outletName && (
                            <span>· {c.franchiseeName}</span>
                          )}
                          {c.city && <span>· {c.city}</span>}
                          {c.pinCode && <span className="font-mono">({c.pinCode})</span>}
                        </div>
                      ))}
                    </div>
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TerritoryCheckerWidget;
